import React from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
import { PageHeader, PageSection, PageSubSection, RelatedContent, CrossLink, IndicationPageIntro, AnchorLinks, PrimaryClaim, SecondaryCallout, KMCurve, FeaturedStat, TextBlock, ReferencesBlock, ComparisonTable, NCCNcallout } from '../../components';
import { StaticImage } from 'gatsby-plugin-image';
import CERVICAL from '../../components/StudyDesign/__study-design/cervical-a18'
import { jobCodes } from '../../util/data/jobCodes';
import styled from 'styled-components';
import { Heading } from '../../util/typography';

// SEO INFORMATION
const pageMeta = {
    title: `KEYNOTE-A18 - Clinical Findings and Study Design`,
    keywords: `keynote a18, keynote-a18, cervical cancer`,
    description: `Health care professionals may find clinical trial results for combination chemoradiotherapy for FIGO 2014 stage 3-4A cervical cancer in KEYNOTE-A18.`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/cervical-cancer-combination-chemoradiotherapy/","@type":"ApprovedIndication","name":"KEYTRUDA and KEYTRUDA QLEX™","alternateName":"pembrolizumab and berahyaluronidase alfa-pmph","description":"KEYTRUDA and KEYTRUDA QLEX™ (pembrolizumab and berahyaluronidase alfa-pmph) Injection 165 mg + 2,000 units/mL are each indicated, in combination with chemoradiotherapy (CRT), for the treatment of adult patients with locally advanced cervical cancer involving the lower third of the vagina, with or without extension to pelvic sidewall, or hydronephrosis/non-functioning kidney, or spread to adjacent pelvic organs (FIGO 2014 Stage III-IVA)."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/cervical-cancer-combination-chemoradiotherapy/","@type":"MedicalAudience","audienceType":"US Health Care Professionals","geographicArea":"This site is intended only for residents of the United States and its territories, including Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/cervical-cancer-combination-chemoradiotherapy/","@type":"MedicalWebPage","name":"Advanced Cervical Cancer","description":"In Combination With Chemoradiotherapy for FIGO 2014 Stage III-IVA Cervical Cancer"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/cervical-cancer-combination-chemoradiotherapy/","@type":"MedicalTrial","name":"Clinical Findings from KEYNOTE⁠-⁠A18","description":"1,060 previously untreated patients with cervical cancer were evaluated in a phase 3 multicenter, randomized, double-blind, placebo-controlled trial","trialDesign":"RandomizedTrial"}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/cervical-cancer-combination-chemoradiotherapy/","@type":"MedicalGuideline","guideline":"Pembrolizumab (KEYTRUDA) and pembrolizumab and berahyaluronidase alfa-pmph (KEYTRUDA QLEX) are the only anti–PD-1 agents in combination with CRT to receive a NCCN category 1 PREFERRED recommendation for the treatment of patients with FIGO 2014 Stage IIIA, IIIB, and IVA cervical cancer","recognizingAuthority":"NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®)"}`
    ]
}

//from /src/util/data/indications.js
// UPDATE THIS TO THE CORRECT INDICATION NUMBER
const indicationId = 37;
const jobCode = jobCodes[24].jobCode;

const ChartTitle = styled(Heading.H4)`
    margin-bottom: 20px;
`;

const relatedContent = [
    {
        section: 'Resources',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    },
    {
        section: 'Resources',
        link: {
            url: 'https://www.merckaccessprogram-keytruda.com/',
            label: 'Access & Reimbursement Support'
        }
    }
];

const crossLinkContent = {
    sections : [
        {
            heading: "Additional Clinical Data for KEYTRUDA",
            items: [
                { text: '1L Combo Therapy Persistent, Recurrent, or Metastatic Cervical Cancer for Tumors Expressing PD&#8288;-&#8288;L1 (CPS ≥1)', url: '/efficacy/cervical-cancer-first-line-combination-therapy/' },
                { text: '2L Monotherapy Recurrent or Metastatic Cervical Cancer for Tumors Expressing PD&#8288;-&#8288;L1 (CPS ≥1)', url: '/efficacy/cervical-cancer-second-line-monotherapy/' },
            ]
        },
        {
            heading: "Related Information",
            items: [
                { text: 'Selected Adverse Reactions', 
                url: '/safety/adverse-reactions/cervical-cancer-combination-chemoradiotherapy/' },
                { text: 'Dosing', url: '/dosing/options/#cer' },
                    ]
        },
    ],
    interestLinks : {
        heading: "You Might Be Interested In",
        items: [
            { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
        ],
    }
};

const anchorLinkModelData = [
    { label: 'Clinical Findings from KEYNOTE&#8288;-&#8288;A18 (KEYTRUDA)' }, 
    { label: 'Study Design for KEYNOTE&#8288;-&#8288;A18 (KEYTRUDA)' }, 
    { label: 'NCCN Clinical Practice Guidelines in Oncology (NCCN Guidelines®)' }, 
]

const pageReferences = [
    {
        label: "1.",
        text: "Referenced with permission from the NCCN Clinical Practice Guidelines In Oncology (NCCN Guidelines®) for Cervical Cancer V.2.2026. © National Comprehensive Cancer Network, Inc. 2025. All rights reserved. Accessed March 17, 2026. To view the most recent and complete version of the guideline, go online to NCCN.org."
    },
   
]

const kmA18Footnotes =[
    {
        label:"b.",
        text: "Based on the unstratified Cox proportional hazard model."
    },
    {
        label:"c.",
        text: "PFS assessed by investigator according to RECIST v1.1."
    },
    {
        label:"d.",
        text: "Results at the time of the first pre-specified interim analysis."
    }
]

const kmA18Definition= "IA1 = first interim analysis."


const survivalFootnotes =[
    {
        label:"c.",
        text: "PFS assessed by investigator according to RECIST v1.1."
    }
]

const oSurvivalFootnotes =[
    {
        label:"b.",
        text: "Based on the unstratified Cox proportional hazard model."
    }
]

const oSurvivalDefinition= "ITT = intent to treat."

const overallSurvivalFootnotes = [
    {
        label:"a.",
        text: "Results at the time of pre-specified final analysis for OS." 
    },
    {
        label: "b.",
        text: "Based on the unstratified Cox proportional hazard model."
    }
]

const overallSurvivalDefinition = "CI = confidence interval; HR = hazard ratio."

const kmOsSecondary = { title : "35% reduction in the risk of death with KEYTRUDA + CRT compared to placebo + CRT (HR<sup>b</sup>=0.65; 95% CI, 0.47–0.90)"}

const osSecondary = "Clinically meaningful improvement in OS with KEYTRUDA + CRT vs CRT alone for patients with FIGO 2014 Stage III&#8288;-&#8288;IVA cervical cancer at final analysis"

const NCCNcalloutData = {
    title: 'Pembrolizumab (KEYTRUDA) and pembrolizumab and berahyaluronidase alfa-pmph (KEYTRUDA QLEX) are the <strong>only anti–PD-1 agents</strong> in combination with CRT to receive a <strong>NCCN category 1 PREFERRED</strong> recommendation for the treatment of patients with FIGO 2014 Stage IIIA, IIIB, and IVA cervical cancer<sup>1</sup>',
    bodyCopy: `
    <p>NCCN Category 1 and PREFERRED recommendation<sup>1,a</sup></p>
    <p><strong>Pembrolizumab (KEYTRUDA) and pembrolizumab and berahyaluronidase alfa-pmph (KEYTRUDA QLEX) + CRT (concurrent platinum containing
chemotherapy + EBRT / brachytherapy radiation) is recommended (category 1) for patients with FIGO 2014 Stage IIIA, IIIB, and IVA cervical cancer<sup>a</sup></strong></p>
<p>KEYTRUDA and KEYTRUDA QLEX are each indicated, in combination with chemoradiotherapy (CRT), for the treatment of adult patients with locally advanced cervical cancer involving the lower third of the vagina, with or without extension to pelvic sidewall, or hydronephrosis/non-functioning kidney, or spread to adjacent pelvic organs (FIGO 2014 Stage III-IVA).</p>

       `,
    showBadge: false,
}

const NCCNcalloutData2 = {
    title: 'FIGO 2014 Stage III-IVA cervical cancer',
    showBadge: false,
}

const NCCNcalloutdefinitions = `
        <p>NCCN makes no warranties of any kind whatsoever regarding their content, use or application and disclaims any responsibility for their application or use in any way.</p>
        <p>Category 1 = Based upon high-level evidence (≥1 randomized phase 3 trials or high-quality, robust meta-analyses), there is uniform NCCN consensus (≥85% support of the Panel) that the intervention is appropriate.<sup>1</sup></p>
        <p>
        According to National Comprehensive Cancer Network® (NCCN®), preferred intervention = intervention that is based on superior efficacy, safety, and evidence; and, when appropriate, affordability.<sup>1</sup>
        </p>
`;

const NCCNcalloutfootnotes = [
    {
        label: "a.",
        text: `Pembrolizumab and berahyaluronidase alfa-pmph subcutaneous injection may be substituted for intravenous pembrolizumab. Pembrolizumab and berahyaluronidase alfa-pmph has different dosing and administration instructions compared to intravenous pembrolizumab.<sup>1</sup>`
    },
];


const Page = ({ location }) => {

    return (
        <DefaultLayout indicationId={indicationId} jobCode={jobCode} pageMeta={ pageMeta } location={ location }>
            <div data-template-name="template-e">

                {/* Begin Intro Column */}
                <TemplateColumn id="template-e-intro" maxWidth={824}>
                    <PageHeader title="Clinical Trial Results for KEYTRUDA® (pembrolizumab)" isBranding />
                   <PageSection bgColor="white">
                        <IndicationPageIntro indicationId={indicationId}/>
                        <AnchorLinks items={anchorLinkModelData} />
                   </PageSection>
                   <PageSection bgColor="gradient">
                        <PrimaryClaim theme="light">KEYTRUDA Is the Only Anti&ndash;PD&#8288;-&#8288;1 Approved to Treat Patients With FIGO 2014 Stage III&#8288;-&#8288;IVA Cervical Cancer With Clinically Meaningful Overall Survival (OS) and Progression-Free Survival (PFS) Data</PrimaryClaim>
                    </PageSection>
                </TemplateColumn>
                {/* End Intro Column */}

                <TemplateColumnWithSidebarContainer>
                {/* Begin Main Column */}
                    <TemplateColumn id="template-e-main">
                        
                        <PageSection bgColor="cloud" title={anchorLinkModelData[0].label}>
                            <PageSubSection title="Overall Survival (OS): FIGO 2014 Stage III-IVA Exploratory Subgroup Analysis at Final Analysis">
                                <p>KEYTRUDA, in combination with chemoradiotherapy (CRT), is indicated for the treatment of patients with locally advanced cervical cancer involving the lower third of the vagina, with or without extension to pelvic sidewall, or hydronephrosis/non-functioning kidney, or spread to adjacent pelvic organs (FIGO 2014 Stage III-IVA).</p>
                                <SecondaryCallout title={osSecondary} />

                                <KMCurve
                                    title="Kaplan-Meier Curve for OS<sup>a</sup> in Patients With FIGO 2014 Stage III&#8288;–&#8288;IVA Cervical Cancer"
                                    hr="HR<sup>b</sup>=0.65 (95% CI: 0.47, 0.90)"
                                    image={() => <StaticImage
                                        src="../../assets/KN-A18_OS_KM.jpg"
                                        placeholder="blurred"
                                        alt="Kaplan-Meier Curve of Overall Survival (OS): FIGO 2014 Stage 3-4A Exploratory Subgroup Analysis at Final Analysis" />}
                                        secondarycallout = {kmOsSecondary}
                                />

                                <TextBlock footnotes={overallSurvivalFootnotes} definitions={overallSurvivalDefinition}>
                                    {/*<ChartTitle as='H3' dangerouslySetInnerHTML={{__html: 'Overall Survival (OS)'}}/>*/}
                                    <ul>
                                        <li>Number of patients with an event: 61/295 (21%) with KEYTRUDA 200 mg IV every 3 weeks and 400 mg IV every 6 weeks + CRT</li>
                                        <li>Number of patients with an event: 90/304 (30%) with Placebo IV + CRT</li>
                                    </ul>
                                </TextBlock>
                            </PageSubSection>
                        </PageSection>

                        <PageSection bgColor="cloud" >
                            <PageSubSection title="Progression&#8288;-&#8288;Free Survival (PFS): FIGO 2014 Stage III-IVA Exploratory Subgroup Analysis at IA1">
                            <SecondaryCallout 
                                title='PFS in patients with FIGO 2014 Stage III–IVA cervical cancer with KEYTRUDA + CRT vs CRT alone at IA1'
                            />
                            <KMCurve
                                    title="Kaplan-Meier Curve for PFS<sup>c,d</sup> in Patients With FIGO 2014 Stage III&#8288;–&#8288;IVA Cervical Cancer"
                                    hr="HR<sup>b</sup>=0.59 (95% CI: 0.43, 0.81)"
                                    footnotes={kmA18Footnotes} 
                                    definitions={kmA18Definition}
                                    image={() => <StaticImage 
                                        src="../../assets/cervical-km-A18_v3.jpg" 
                                        placeholder="blurred" 
                                        alt="Kaplan-Meier Curve for PFS in Patients With FIGO 2014 Stage 3-4A Cervical Cancer" />}  
                                />

                            <ComparisonTable
                                    title="Progression-Free Survival (PFS)<sup>c</sup>"
                                    footnotes={survivalFootnotes}
                                    definitions="NR = not reached."
                                    data={
                                        [
                                            [
                                                {
                                                    legendTitle: 'KEYTRUDA 200&nbsp;mg every 3 weeks and 400 mg every 6 weeks + CRT (n=295)',
                                                },
                                                {
                                                    legendTitle: 'Placebo + CRT (n=304)',
                                                },
                                            ],
                                            [
                                                {
                                                    label: 'Number of patients with event (%)',
                                                    dataPoint: '61',
                                                    dataSubPoint: '(21%)',
                                                },
                                                {
                                                    label: 'Number of patients with event (%)',
                                                    dataPoint: '94',
                                                    dataSubPoint: '(31%)',
                                                },
                                            ],
                                            [
                                                {
                                                    label: 'Median PFS in months',
                                                    dataPoint: 'NR',
                                                    dataSubPoint: '(95% CI, NR–NR)',
                                                },
                                                {
                                                    label: 'Median PFS in months',
                                                    dataPoint: 'NR',
                                                    dataSubPoint: '(95% CI, 18.8–NR)',
                                                },
                                            ],
                                            [
                                                {
                                                    label: '12-month PFS rate',
                                                    dataPoint: '81%',
                                                    dataSubPoint: '(95% CI, 75–85)',
                                                },
                                                {
                                                    label: '12-month PFS rate',
                                                    dataPoint: '70%',
                                                    dataSubPoint: '(95% CI, 64–76) ',
                                                },
                                            ],
                                        ]
                                    }
                                    
                                />

                                <SecondaryCallout
                                    title="41% reduction in the risk of disease progression or death with KEYTRUDA + CRT compared to placebo + CRT (HR<sup>b</sup>=0.59; 95% CI, 0.43–0.81)"
                                />
                                <TextBlock footnotes={oSurvivalFootnotes} definitions={oSurvivalDefinition}>
                                    <ul>
                                        <li>An exploratory subgroup analysis of PFS and OS by the stratification factor of FIGO 2014 stage showed that the statistically significant improvement in the ITT population was primarily attributed to the results seen in the patients with FIGO 2014 Stage III&#8288;-&#8288;IVA disease</li>
                                    </ul>
                                </TextBlock>

                            </PageSubSection>
                        </PageSection>

                        <PageSection title={anchorLinkModelData[1].label} >
                                <CERVICAL />
                        </PageSection>

                        <PageSection bgColor="cloud" title={anchorLinkModelData[2].label}>
                            <NCCNcallout
                                data={NCCNcalloutData}
                                definitions={NCCNcalloutdefinitions}
                                footnotes={NCCNcalloutfootnotes}
                            />
                        </PageSection>
                    </TemplateColumn>

                    {/* End Main Column */}
                    <TemplateColumn id="template-e-sidebar">
                        <PageSection bgColor="mist">
                            <CrossLink {...crossLinkContent} />
                        </PageSection>
                    </TemplateColumn>

                </TemplateColumnWithSidebarContainer>
                
                <TemplateColumn id="template-e-secondary">
                    <PageSection bgColor="keytrudaGreen">
                        <RelatedContent items={relatedContent}/>
                    </PageSection>

                    <PageSection bgColor="mist">
                        <ReferencesBlock references={pageReferences} />
                    </PageSection>
                </TemplateColumn>
            </div>
        </DefaultLayout>
    );
};

export default Page;